import { useMemo, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Plus, Edit, Trash2, Upload, X, FileUp } from "lucide-react";
import LineDialog from "../../components/LineDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { parseCsv, looksLikeHeaderRow , readTextFile } from "../../lib/parseCsv";
import DuplicateBadge, { type DuplicateKind } from "../../components/DuplicateBadge";
import TablePagination, { type PaginationMeta } from "../../components/TablePagination";
import { OFFER_TAB_PROPS } from "../../lib/offerTabs";
import { lineKey } from "../../lib/utils";
interface Tag {
  id: number;
  name: string;
  color: string;
}

interface SubjectLine {
  id: number;
  description: string;
  tag_ids?: number[];
  tags?: Tag[];
  created_at: string;
}

interface SubjectLinesProps {
  offer: {
    id: number;
    name: string;
  };
  subject_lines: SubjectLine[];
  pagination: PaginationMeta;
  /** Every saved line, not just this page -- the duplicate check needs them all */
  all_descriptions: string[];
  tags: Tag[];
  is_admin: boolean;
}

// A subject line staged for creation but not yet saved
interface DraftLine {
  key: string;
  description: string;
  tagId: number | null;
}

const NO_TAG = "none";

let draftCounter = 0;
const nextKey = () => `draft-${draftCounter++}`;

export default function SubjectLines({
  offer,
  subject_lines,
  pagination,
  all_descriptions,
  tags,
  is_admin,
}: SubjectLinesProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SubjectLine | null>(null);
  const [itemToDelete, setItemToDelete] = useState<SubjectLine | null>(null);

  // Staging area state
  const [drafts, setDrafts] = useState<DraftLine[]>([]);
  const [newDescription, setNewDescription] = useState("");
  const [newTagId, setNewTagId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tagByName = (name: string): number | null => {
    const match = tags.find(
      (t) => t.name.toLowerCase() === name.trim().toLowerCase()
    );
    return match ? match.id : null;
  };

  // Every line already saved on this offer -- built from the full list rather
  // than the visible page, or a duplicate on page 2 would slip through
  const savedKeys = useMemo(
    () => new Set(all_descriptions.map((d) => lineKey(d))),
    [all_descriptions]
  );
  // Flag each staged line: already saved on the offer, or repeated in the CSV
  const draftFlags = useMemo(() => {
    const seen = new Set<string>();
    return drafts.map((draft) => {
      const key = lineKey(draft.description);
      const kind: DuplicateKind | null = savedKeys.has(key)
        ? "existing"
        : seen.has(key)
          ? "repeat"
          : null;
      seen.add(key);
      return kind;
    });
  }, [drafts, savedKeys]);

  const duplicateDraftCount = draftFlags.filter(Boolean).length;

  const removeDuplicateDrafts = () => {
    setDrafts((prev) => prev.filter((_, i) => !draftFlags[i]));
  };

  const handleAddToList = () => {
    const description = newDescription.trim();
    if (!description) {
      toast.error("Enter a subject line description");
      return;
    }
    setDrafts((prev) => [
      ...prev,
      { key: nextKey(), description, tagId: newTagId },
    ]);
    setNewDescription("");
    setNewTagId(null);
  };

  const handleImportCsv = async(file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("That isn't a CSV file", {
        description: "Choose a .csv file to import",
      });
      return;
    }

    let text: string;
    try{
        text = await readTextFile(file);
    }catch{
        toast.error("Could not read that file",{
          description: "Please try again"
        });
        return;
    }

    const rows = parseCsv(text);
    if (rows.length === 0){
      toast.error("The CSV is empty");
      return;
    }

    const body = looksLikeHeaderRow(rows[0]) ? rows.slice(1) : rows;
    const imported: DraftLine[] = body
      .map((row) => ({
        key: nextKey(),
        description: (row[0] || "").trim(),
        // Optional second column maps to a tag by name
        tagId: row[1] ? tagByName(row[1]) : null,
      }))
      .filter((d) => d.description.length > 0);

    if (imported.length === 0) {
      toast.error("No subject lines found in the CSV");
      return;
    }

    // Count what the CSV repeats, so the toast says so up front
    const seen = new Set([
      ...savedKeys,
      ...drafts.map((d) => lineKey(d.description)),
    ]);
    let duplicates = 0;
    for (const line of imported) {
      const key = lineKey(line.description);
      if (seen.has(key)) duplicates++;
      seen.add(key);
    }

    setDrafts((prev) => [...prev, ...imported]);
    if (duplicates > 0){
      toast.warning(
      `Imported ${imported.length} subject ${imported.length === 1 ? "line" : "lines"}`,
      {
        description: `${duplicates} ${duplicates === 1 ? "is a duplicate" : "are duplicates"} and ${duplicates === 1 ? "is" : "are"} marked below -- remove or create them as you like`

      }
    );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImportCsv(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImportCsv(file);
  };

  const updateDraftTag = (key: string, tagId: number | null) => {
    setDrafts((prev) =>
      prev.map((d) => (d.key === key ? { ...d, tagId } : d))
    );
  };

  const removeDraft = (key: string) => {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  };

  const handleCreate = () => {
    if (drafts.length === 0) {
      toast.error("Add at least one subject line first");
      return;
    }

    if (duplicateDraftCount > 0) {
      const existing = draftFlags.filter((f) => f === "existing").length;
      const repeats = draftFlags.filter((f) => f === "repeat").length;
      toast.error(
        `Resolve ${duplicateDraftCount} duplicate ${duplicateDraftCount === 1 ? "line" : "lines"} first`,
        {
          description: [
            existing > 0 &&
              `${existing} already ${existing === 1 ? "exists" : "exist"} in the list`,
            repeats > 0 &&
              `${repeats} ${repeats === 1 ? "is repeated" : "are repeated"} in the CSV`,
          ]
            .filter(Boolean)
            .join(" and "),
        }
      );
      return;
    }

    setSubmitting(true);
    router.post(
      `/offers/${offer.id}/subject_lines/bulk_create`,
      {
        subject_lines: drafts.map((d) => ({
          description: d.description,
          tag_ids: d.tagId ? [d.tagId] : [],
        })),
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setDrafts([]);
          setNewDescription("");
          setNewTagId(null);
        },
        onFinish: () => setSubmitting(false),
      }
    );
  };

  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const handleDeleteAll = () => {
    setDeletingAll(true);
    router.delete(`/offers/${offer.id}/subject_lines/destroy_all`, {
      preserveScroll: true,
      onFinish: () => {
        setDeletingAll(false);
        setDeleteAllConfirmOpen(false);
      },
    });
  };

  const handleEdit = (item: SubjectLine) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleDelete = (item: SubjectLine) => {
    setItemToDelete(item);
    setConfirmDialogOpen(true);
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) setSelectedItem(null);
  };

  const handleConfirmDialogChange = (open: boolean) => {
    setConfirmDialogOpen(open);
    if (!open) setItemToDelete(null);
  };

  const getColorClass = (color: string) => {
    switch (color) {
      case "green":
        return "bg-green-100 text-green-800 border-green-200";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "red":
        return "bg-red-100 text-red-800 border-red-200";
      case "blue":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "purple":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const tagValue = (tagId: number | null) =>
    tagId === null ? NO_TAG : String(tagId);
  const parseTagValue = (value: string) =>
    value === NO_TAG ? null : Number(value);

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Subject Lines</h2>
        <p className="text-sm text-gray-600 mt-1">
          Add and manage subject lines for this offer.
        </p>
      </div>

      {is_admin && (
        <Card className="mb-6">
          <CardContent className="p-6 space-y-6">
            {/* Upload CSV */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Upload CSV
              </h3>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 cursor-pointer transition-colors ${
                  isDragging
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Upload className="w-4 h-4" />
                  Choose File
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Manual add */}
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Line <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleAddToList();
                    }
                  }}
                  rows={2}
                  placeholder="Enter subject line..."
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="w-full md:w-64">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tag
                </label>
                <Select
                  value={tagValue(newTagId)}
                  onValueChange={(v) => setNewTagId(parseTagValue(v))}
                >
                  <SelectTrigger className="w-full cursor-pointer">
                    <SelectValue placeholder="Select tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TAG}>Select tag</SelectItem>
                    {tags.map((tag) => (
                      <SelectItem key={tag.id} value={String(tag.id)}>
                        {tag.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={handleAddToList}
                className="cursor-pointer border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add to List
              </Button>
            </div>

            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <FileUp className="w-3.5 h-3.5" />
              Add subject lines manually using the form above or upload them in
              bulk using CSV. CSV: one subject line per row, optional second
              column for the tag name.
            </p>

            {/* Staged list */}
            {drafts.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-[1fr_16rem_auto] gap-4 px-1 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <span>Subject Line</span>
                  <span>Tag</span>
                  <span className="sr-only">Remove</span>
                </div>
                <div className="space-y-2">
                  {drafts.map((draft, index) => (
                    <div
                      key={draft.key}
                      className="grid grid-cols-[1fr_16rem_auto] items-center gap-4"
                    >
                      <span className="flex items-center gap-2 text-sm text-gray-800">
                        <span>{draft.description}</span>
                        {draftFlags[index] && (
                          <DuplicateBadge kind={draftFlags[index]!} />
                        )}
                      </span>
                      <Select
                        value={tagValue(draft.tagId)}
                        onValueChange={(v) =>
                          updateDraftTag(draft.key, parseTagValue(v))
                        }
                      >
                        <SelectTrigger className="w-full cursor-pointer">
                          <SelectValue placeholder="Select tag" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_TAG}>Select tag</SelectItem>
                          {tags.map((tag) => (
                            <SelectItem key={tag.id} value={String(tag.id)}>
                              {tag.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => removeDraft(draft.key)}
                        className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-end gap-3 mt-4">
                  {duplicateDraftCount > 0 && (
                    <>
                      <p className="mr-auto text-sm text-amber-700">
                        {duplicateDraftCount}{" "}
                        {duplicateDraftCount === 1
                          ? "duplicate is"
                          : "duplicates are"}{" "}
                        marked below. Create them anyway or remove them.
                      </p>
                      <Button
                        variant="outline"
                        onClick={removeDuplicateDrafts}
                        disabled={submitting}
                        className="cursor-pointer border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      >
                        Remove {duplicateDraftCount}{" "}
                        {duplicateDraftCount === 1 ? "duplicate" : "duplicates"}
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={handleCreate}
                    disabled={submitting}
                    className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 cursor-pointer"
                  >
                    {submitting
                      ? "Creating..."
                      : `Create ${drafts.length} subject ${drafts.length === 1 ? "line" : "lines"}`}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Persisted list */}
      {is_admin && pagination.total_count > 0 && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">
            {pagination.total_count} subject{" "}
            {pagination.total_count === 1 ? "line" : "lines"}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteAllConfirmOpen(true)}
            className="cursor-pointer border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All
          </Button>
        </div>
      )}

      <Card className="py-0 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject Lines
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  {is_admin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subject_lines.length === 0 ? (
                  <tr>
                    <td
                      colSpan={is_admin ? 4 : 3}
                      className="px-6 py-12 text-center"
                    >
                      <div className="flex flex-col items-center">
                        <div className="rounded-full bg-gray-100 p-3 mb-4">
                          <Plus className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-gray-500 text-sm">
                          No subject lines added yet
                        </p>
                        {is_admin && (
                          <p className="text-gray-400 text-xs mt-1">
                            Add one above or upload a CSV to get started
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  subject_lines.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-2 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.description}
                        </div>
                      </td>
                      <td className="px-6 py-2">
                        <div className="flex flex-wrap gap-1">
                          {item.tags && item.tags.length > 0 ? (
                            item.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getColorClass(
                                  tag.color
                                )}`}
                              >
                                {tag.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.created_at).toLocaleDateString()}
                      </td>
                      {is_admin && (
                        <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(item)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(item)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <TablePagination
        pagination={pagination}
        reloadOnly={OFFER_TAB_PROPS.subject_lines}
        pageParam="subject_lines_page"
        label="subject lines"
      />

      <Dialog open={deleteAllConfirmOpen} onOpenChange={setDeleteAllConfirmOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete all subject lines</DialogTitle>
            <DialogDescription>
              This permanently deletes all {pagination.total_count} subject{" "}
              {pagination.total_count === 1 ? "line" : "lines"} for this offer.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteAllConfirmOpen(false)}
              disabled={deletingAll}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={deletingAll}
            >
              {deletingAll ? "Deleting..." : "Delete All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LineDialog
        open={dialogOpen}
        onOpenChange={handleDialogChange}
        offerId={offer.id}
        item={selectedItem}
        availableTags={tags}
      />

      {itemToDelete && (
        <ConfirmationDialog
          open={confirmDialogOpen}
          onOpenChange={handleConfirmDialogChange}
          title="Delete Subject Line"
          description={`Are you sure you want to delete "${itemToDelete.description}"? This action cannot be undone.`}
          offerId={offer.id}
          resourceType="subject_lines"
          itemId={itemToDelete.id}
        />
      )}
    </>
  );
}